import React from 'react';
import { Check, ChevronRight, Globe, MapPin, Shield } from 'lucide-react';
import { useSession } from '@/manager-next/useRqSession';
import Avatar from '@/components/Avatar';
import { SignOut } from '@/components/auth-components';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { Modal } from '@lushdigital/ui-latest';
import { languageConfig } from '@/helpers/common';
import { getHighestPermissionRole } from '@/helpers/common';

export const AccountDropdown = (): JSX.Element => {
  const { t } = useTranslation('tills');
  const router = useRouter();
  const [session] = useSession();
  const { locale } = router;
  const [selectedLanguage, setSelectedLanguage] = React.useState(locale);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const user = session
    ? {
        first_name: session.user?.firstName,
        last_name: session.user?.lastName,
        name: session.user?.name,
        email: session.user?.email,
        profile_image: session.user?.user?.profile_image,
        regions: session?.user?.userRegions
      }
    : {};

  const currentLanguage = languageConfig.find((lang) => lang.code === selectedLanguage);

  const renderLocaleLinks = (): JSX.Element[] => {
    return languageConfig.map((lang) => {
      return (
        <a
          href={`/${lang.code}${router.asPath}`}
          key={lang.code}
          className={`flex items-center justify-between px-6 py-4 border border-gray-200 transition-colors duration-200 hover:bg-gray-50 ${
            selectedLanguage === lang.code ? 'bg-gray-100' : 'bg-white'
          }`}
        >
          <div className="flex items-center">
            <span className="text-2xl mr-4">{lang.flag}</span>
            <span className="text-lg font-medium text-gray-900">{lang.name}</span>
          </div>

          {selectedLanguage === lang.code && <Check className="h-5 w-5 text-green-600" />}
        </a>
      );
    });
  };

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <div className="flex items-center justify-center">
        <div className="relative" ref={dropdownRef}>
          <button type="button" onClick={() => setIsOpen(!isOpen)}>
            <Avatar first_name={user?.first_name} last_name={user?.last_name} showNameOnHover={false} />
          </button>

          {isOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center space-x-3 mb-3">
                  <Avatar
                    first_name={user?.first_name}
                    last_name={user?.last_name}
                    showNameOnHover={false}
                    className="!bg-black !text-white"
                  />
                  <div className="flex flex-col">
                    <p className="text-sm font-medium text-gray-900 text-left">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Shield className="h-4 w-4 text-gray-400" />
                    <span className="text-xs text-gray-500">{t('PERMISSION_LEVEL')}:</span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {t(getHighestPermissionRole(session?.user?.roles) || 'STORE_MANAGER')}
                    </span>
                  </div>

                  {user.regions && user.regions.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="text-xs text-gray-500">{t('REGION')}:</span>
                      <div className="flex flex-wrap gap-1">
                        {user.regions.map((region, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            {region}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setIsLanguageModalOpen(true);
                      setIsOpen(false);
                    }}
                    className="flex items-center justify-between w-full p-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 transition-all duration-200 group"
                  >
                    <div className="flex items-center space-x-2">
                      <Globe className="h-4 w-4 text-gray-400" />
                      <span className="text-xs font-medium text-gray-900 flex items-center space-x-1">
                        {currentLanguage?.name}
                      </span>
                    </div>
                    <ChevronRight className="h-3 w-3 text-gray-400 group-hover:text-gray-600 transition-colors duration-200" />
                  </button>
                </div>
              </div>

              <div className="p-2">
                <SignOut
                  key="signout"
                  theme="inverse"
                  className="!justify-start !px-3 !rounded-lg hover:bg-neutral-200 !hover:text-yellow-400"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {isLanguageModalOpen && (
        <Modal header={t('SELECT_LANGUAGE')} onClose={() => setIsLanguageModalOpen(false)}>
          <div className="flex flex-col gap-5">{renderLocaleLinks()}</div>
        </Modal>
      )}
    </>
  );
};
